import React from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import PlayerRowContent from './PlayerRowContent';
import ScoreRowContent from './ScoreRowContent';

import { getScoresForPosition, getPlayerTag, getPositionPlayers, getLiveScoreDisplay, isMatchLive, isMatchUpcoming, isMatchCompleted } from '../../../../../utils/matchCardUtils';
import { showMatchWinner } from '../../../../../utils/postScore';
import { isEventOfficialHsEvent } from '../../../../../utils/events';
import { sportTypeIds, utrTypeIds } from '../../../../../utils/eventConstants';

import styles from './eventProfileMatchCard.scss';

const MatchPositionContent = (props) => {
    const {
        position,
        hasPositionWon = false,
        match,
        draw,
        event,
        isDoubles,
        showSeparator = true,
        isLiveScoresEnabled = false,
        isCurrentMemberAdmin = false,
    } = props;

    const coreUI = useSelector(({ coreUI }) => coreUI);
    const drawTagTypes = coreUI?.types?.tms?.draw?.drawTagTypes || [];
    const utrTypes = coreUI?.types?.tms?.utrTypes ?? [];

    const auth = useSelector(({ auth }) => auth);
    const userLoggedIn = !!auth?.user;

    const isOfficialHSEvent = isEventOfficialHsEvent(event);
    const utrTypeId = event?.utrTypeId ?? utrTypeIds.MYUTR;
    const sportTypeId = event?.sportTypeId ?? sportTypeIds.TENNIS;

    const participatingMembers = match?.participatingMembers ?? [];
    const positionalPlayers = getPositionPlayers(match, position.id, participatingMembers);
    const tag = getPlayerTag(match?.[position.key], drawTagTypes);
    const seed = match?.[position.seedKey];

    const isProgressionDraw = draw?.ballTypeId !== null;
    const isUpcoming = isMatchUpcoming(match);
    const shortenFirstName = (match != null && isMatchCompleted(match)) || !isUpcoming;

    const positionScores = getScoresForPosition(match, position.id, isLiveScoresEnabled);

    const shouldDisplayLiveData = isLiveScoresEnabled && isMatchLive(match);
    const gameState = shouldDisplayLiveData ? match.liveMatch?.gameState : null;

    const liveScore = shouldDisplayLiveData ? getLiveScoreDisplay(gameState, position.liveScoreKey) : null;
    const isPositionServing = shouldDisplayLiveData && (match.liveMatch?.[position.key]?.isServing === true || gameState?.serving === position.liverServeStatusKey);

    const showWinnerArrow =
        (match?.result && showMatchWinner(match.result.outcome, match.result.completionType)) ||
        (isMatchCompleted(match) && !!match.liveMatch?.periodScores?.length && !match.result);

    return (
        <>
            <div className={styles.positionContent}>
                <div className={styles.playersData}>
                    {positionalPlayers.map((player, index) => (
                        <PlayerRowContent
                            key={`player-row-content-${player?.memberId ?? ''}`}
                            playerData={player}
                            shortenFirstName={shortenFirstName}
                            seed={isDoubles && index > 0 ? null : seed}
                            tag={tag}
                            isDoubles={isDoubles}
                            showRatings={userLoggedIn}
                            sportTypeId={sportTypeId}
                            isProgressionDraw={isProgressionDraw}
                            formatTypeId={draw?.formatTypeId}
                            utrTypeId={utrTypeId}
                            utrTypes={utrTypes}
                            isOfficialHSEvent={isOfficialHSEvent}
                            isCurrentMemberAdmin={isCurrentMemberAdmin}
                            hasPositionWon={hasPositionWon}
                        />
                    ))}
                </div>
                <ScoreRowContent
                    keyPrefix={position.key}
                    scores={positionScores}
                    liveScore={liveScore}
                    isServing={isPositionServing}
                    shouldDisplayLiveData={shouldDisplayLiveData}
                    wrapperClassName={styles.teamScores}
                />
                {hasPositionWon && showWinnerArrow && (
                    <div className={styles.winnerArrow} />
                )}
            </div>
            {showSeparator && (
                <div className={styles.playerSeparator} />
            )}
        </>
    );
};

MatchPositionContent.propTypes = {
    position: PropTypes.shape({
        id: PropTypes.string.isRequired,
        value: PropTypes.string.isRequired,
        key: PropTypes.string.isRequired,
        hasPositionWon: PropTypes.bool.isRequired,
        seedKey: PropTypes.string.isRequired,
        liveScoreKey: PropTypes.string.isRequired,
        liverServeStatusKey: PropTypes.string.isRequired,
    }).isRequired,
    hasPositionWon: PropTypes.bool,
    match: PropTypes.shape({
        position1: PropTypes.object,
        position2: PropTypes.object,
        position1Seed: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        position2Seed: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        result: PropTypes.object,
        participatingMembers: PropTypes.array,
        facilityAbbreviation: PropTypes.string,
        courtName: PropTypes.string,
        isLive: PropTypes.bool,
        liveMatch: PropTypes.object,
        isTimeBased: PropTypes.bool,
        roundNumber: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }).isRequired,
    draw: PropTypes.shape({
        ballTypeId: PropTypes.string,
        formatTypeId: PropTypes.string,
    }).isRequired,
    event: PropTypes.shape({
        utrTypeId: PropTypes.string,
        sportTypeId: PropTypes.string,
    }).isRequired,
    isDoubles: PropTypes.bool,
    showSeparator: PropTypes.bool,
    isCurrentMemberAdmin: PropTypes.bool,
    isLiveScoresEnabled: PropTypes.bool,
};

MatchPositionContent.defaultProps = {
    hasPositionWon: false,
    isDoubles: false,
    showSeparator: true,
    isCurrentMemberAdmin: false,
    isLiveScoresEnabled: false,
};

export default MatchPositionContent;
